<script lang="ts">
	//Formatters
	import { format } from 'svelte-i18n';
	//Helpers
	import { real_id } from 'src/helpers/interface_helpers';
	import { onSettingsResponse } from '../../../helpers/device_settings_helpers';
	import {
		getNetMeteringPeriods,
		isExperimentalNetMeteringEnabled,
		isSupportNetMetering,
	} from 'src/helpers/statistics/core/net_metering/chart_net_metering_support_helpers';
	//Stores
	import { devices } from 'src/stores/lists/devices';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Services
	import { postReq } from '../../../services/http';
	//Design system components
	import { Text, Alert, Toggle, Token } from '@Shelly/shelly-design-system';
	//Types
	import type { Device } from '../../../types/Device';
	import type { InputClickEvent } from '../../../types/InputClickEvent';

	export let device: Device;

	const id = device.id;
	const mainDevice = $devices[real_id(id)];

	let peakPower = device?.cloud_options?.measure_peak_power || false;
	let loadingPeakPowerRequest = false;

	const toastTexts = {
		success: $format('toast_succ_measure_peak_power', {
			default: 'Device enabled for "Peak Power" measurements.',
		}),
		danger: $format('setting_err_toast', {
			default: 'Failed to apply settings.',
		}),
	};

	const saveNoRoomCons = async (e) => {
		device.no_room_cons = e.target.checked;
		const res = await postReq(`/interface/device/save`, {
			id,
			data: JSON.stringify(device),
		});
		let toastTexts = {};
		if (res?.isok) {
			toastTexts = {
				success: device.no_room_cons
					? $format('device_excluded_from_total_room_scc_toast', {
							default: 'The device is successfully excluded from room consumption.',
						})
					: $format('device_included_to_total_room_scc_toast', {
							default: 'The device is successfully included to total room consumption.',
						}),
			};
		} else {
			device.no_room_cons = !device.no_room_cons;
		}
		onSettingsResponse(res, device, toastTexts);
	};

	const saveNoAccountCons = async (e) => {
		device.no_account_cons = e.target.checked;
		const res = await postReq(`/interface/device/save`, {
			id,
			data: JSON.stringify(device),
		});
		let toastTexts = {};
		if (res?.isok) {
			toastTexts = {
				success: device.no_account_cons
					? $format('device_excluded_from_total_account_scc_toast', {
							default:
								'The device is successfully excluded from total account consumption.',
						})
					: $format('device_included_to_total_account_scc_toast', {
							default: 'The device is successfully included to total account consumption.',
						}),
			};
		} else {
			device.no_account_cons = !device.no_account_cons;
		}
		onSettingsResponse(res, device, toastTexts);
	};

	const savePeakPower = async (e: InputClickEvent) => {
		peakPower = e.target.checked;

		loadingPeakPowerRequest = true;
		const res = await postReq('interface/device/set_cloud_options', {
			id: device.id,
			set: 'measure_peak_power',
			val: peakPower,
		});
		loadingPeakPowerRequest = false;

		if (res?.isok) {
			if (!device.cloud_options) {
				device.cloud_options = {};
			}
			device.cloud_options.measure_peak_power = peakPower;
			devices.update(device);
		} else {
			peakPower = !peakPower;
		}
		onSettingsResponse(res, device, toastTexts);
	};

	const netMeteringPeriods = getNetMeteringPeriods();

	$: selectNetMeteringPeriod =
		netMeteringPeriods.find((v) => v.value === mainDevice?.cloud_options?.measure_net_energy)
			?.value || 0;
	let cacheNetMeteringPeriod: number = selectNetMeteringPeriod;

	const saveNetMeteringPeriod = async (value) => {
		if (!mainDevice) return;

		if (!mainDevice.cloud_options) {
			mainDevice.cloud_options = {};
		}

		if (mainDevice.cloud_options.measure_net_energy) {
			cacheNetMeteringPeriod = mainDevice.cloud_options.measure_net_energy;
		}

		mainDevice.cloud_options.measure_net_energy = value;

		const res = await postReq('interface/device/set_cloud_options', {
			id: mainDevice.id,
			set: 'measure_net_energy',
			val: value,
		});

		let toastTexts = {};

		if (res?.isok) {
			toastTexts = {
				success: mainDevice.cloud_options.measure_net_energy
					? $format('device_net_metering_enabled_toast', {
							values: {
								period: mainDevice.cloud_options.measure_net_energy,
							},
							default: `The device 'Net metering' statistics enabled with a period of ${mainDevice.cloud_options.measure_net_energy}.`,
						})
					: $format('device_net_metering_disabled_toast', {
							default: "The device 'Net metering' statistics disabled.",
						}),
			};
		} else {
			mainDevice.cloud_options.measure_net_energy = cacheNetMeteringPeriod;
		}
		onSettingsResponse(res, mainDevice, toastTexts);
	};
</script>

<Toggle
	class={$showNewLayout ? 'mb-s' : ''}
	checked={device?.no_room_cons}
	size="m"
	on:click={saveNoRoomCons}
	id="toggle-f5smn59"
>
	{$format('exclude_from_total_room_energy', {
		default: 'Exclude from total room energy.',
	})}
</Toggle>

<Toggle
	class={$showNewLayout ? 'mb-s' : ''}
	checked={device?.no_account_cons}
	size="m"
	on:click={saveNoAccountCons}
	id="toggle-vhaq2ig"
>
	{$format('exclude_from_total_total_energy', {
		default: 'Exclude from total account energy.',
	})}
</Toggle>

{#if device.category !== 'light'}
	<hr class="mt-s mb-m" />

	<Toggle bind:checked={peakPower} size="m" on:click={savePeakPower} id="toggle-i9u50rr">
		{$format('label_measure_peak_power', {
			default: 'Use device to record peak consumption.',
		})}
	</Toggle>
{/if}

{#if isExperimentalNetMeteringEnabled() && isSupportNetMetering(device)}
	<hr class="mt-s mb-m" />

	<Alert
		class="mb-s"
		type="info"
		heading={$format('feature_nm_name', {
			default: 'Net Metering Support',
		})}
		isHeadingBold
		message={$format('description_net_metering_feature', {
			default:
				'Net Metering is an experimental feature. Statistics will only start recording once a Net Metering period is selected.',
		})}
		cta={{
			title: $format('go_to_statistics', {
				default: 'Go to statistics',
			}),
		}}
		on:cta_click={() => navigateToSetting(device.id, 'charts')}
	/>

	<Text class="mb-s">
		{$format('text_net_metering_period', {
			default: 'Net metering period',
		})}
	</Text>

	<div class="d-flex flex-wrap">
		{#each netMeteringPeriods as period}
			<Token
				id="token-yunxrig"
				size="m"
				title={period.title}
				class="mb-s mr-s"
				selected={period.value === selectNetMeteringPeriod}
				on:click={() => saveNetMeteringPeriod(period.value)}
			/>
		{/each}
	</div>
{/if}
